import { useEffect, useRef, type ReactNode } from "react";

import { MarketingLink } from "@/components/marketing/MarketingButton";
import {
  MarketingSection,
  SectionIntro,
  SectionKicker,
  SectionTitle,
} from "@/components/marketing/MarketingSection";
import { MARKETING_PRODUCTS } from "@/components/marketing/products";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { cn } from "@/lib/utils";

const CARD_CLASS = "flex min-w-0 flex-col";

function MediaStage({
  className,
  linked = false,
  children,
}: {
  className: string;
  /** Lifts the visual when the surrounding card link is hovered. */
  linked?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="flex h-[clamp(270px,27vw,340px)] w-full items-end justify-start max-[680px]:h-[310px]">
      <div
        className={cn(
          "relative overflow-hidden rounded-[17px] shadow-[0_18px_42px_rgba(16,28,84,0.15)] [&_img]:block [&_img]:size-full [&_img]:object-cover",
          linked &&
            "transition-[transform,box-shadow] duration-200 group-hover:shadow-[0_24px_52px_rgba(16,28,84,0.2)] group-focus-visible:shadow-[0_24px_52px_rgba(16,28,84,0.2)] motion-safe:group-hover:-translate-y-[5px] motion-safe:group-focus-visible:-translate-y-[5px]",
          className,
        )}
      >
        {children}
      </div>
    </div>
  );
}

function CardTitle({ children }: { children: ReactNode }) {
  return (
    <h3 className="mb-2 mt-6 font-figtree text-[23px] font-[850] leading-[1.15]">
      {children}
    </h3>
  );
}

function CardBody({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <p
      className={cn(
        "m-0 font-open-sans text-[16px] leading-[1.55] text-marketing-muted",
        className,
      )}
    >
      {children}
    </p>
  );
}

function ContextLink({ children }: { children: ReactNode }) {
  return (
    <span className="mt-auto block pt-[18px] font-open-sans text-[14px] font-[850] leading-[1.55] text-blue-600">
      {children}
    </span>
  );
}

function CanvasVideo() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (reduceMotion) {
      video.pause();
    } else {
      video.play().catch(() => {});
    }
  }, [reduceMotion]);

  return (
    <video
      ref={videoRef}
      autoPlay={!reduceMotion}
      muted
      loop
      playsInline
      preload="metadata"
      poster="/marketing/artwork/artwork-workflow-canvas-poster.webp"
      aria-label="Rainforest and yellow tent artwork animated for Spotify Canvas"
      className="block size-full object-cover"
    >
      <source
        src="/marketing/artwork/artwork-workflow-canvas.mp4"
        type="video/mp4"
      />
    </video>
  );
}

export default function ArtworkWorkflow() {
  return (
    <MarketingSection className="bg-[#F7F8FC]">
      <SectionKicker>Connected artwork workflow</SectionKicker>
      <SectionTitle>Your cover is just the beginning.</SectionTitle>
      <SectionIntro>
        Use your artwork on your release, bring it into a lyric video, or turn
        it into motion artwork. Keep building around the same visual idea, with
        everything connected to your music in Lutely.
      </SectionIntro>
      <div className="mt-12 grid grid-cols-3 items-stretch gap-[clamp(28px,4vw,58px)] max-[980px]:grid-cols-2 max-[680px]:grid-cols-1">
        <article className={CARD_CLASS}>
          <MediaStage className="aspect-square h-[88%]">
            <img
              src="/marketing/artwork/artwork-workflow-release.webp"
              alt="Rainforest and yellow tent cover art"
              loading="lazy"
            />
          </MediaStage>
          <CardTitle>Cover art</CardTitle>
          <CardBody>Use the finished artwork on your single or album.</CardBody>
        </article>
        <MarketingLink
          to={MARKETING_PRODUCTS.lyricVideo.path}
          className={cn(CARD_CLASS, "group")}
        >
          <MediaStage linked className="aspect-[1370/768] w-full">
            <img
              src="/marketing/artwork/artwork-workflow-lyric-video.webp"
              alt="Rainforest and yellow tent artwork used in a lyric video"
              loading="lazy"
            />
          </MediaStage>
          <CardTitle>Lyric video</CardTitle>
          <CardBody>
            Carry the same visual direction into your lyric video.
          </CardBody>
          <ContextLink>Explore Lyric Videos →</ContextLink>
        </MarketingLink>
        <MarketingLink
          to={MARKETING_PRODUCTS.motion.path}
          className={cn(CARD_CLASS, "group min-[981px]:pl-[12%]")}
        >
          <MediaStage linked className="aspect-[1080/1920] h-[88%]">
            <CanvasVideo />
          </MediaStage>
          <CardTitle>Motion artwork</CardTitle>
          <CardBody className="min-[981px]:w-[clamp(134px,13.365vw,168px)] min-[981px]:text-[14px]">
            Bring your cover to life
            <br />
            for music platforms.
          </CardBody>
          <ContextLink>Explore Motion Artwork →</ContextLink>
        </MarketingLink>
      </div>
    </MarketingSection>
  );
}
